import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import CyclingSelector from '../CyclingSelector';
import {TaggedUnionUISchema} from '../StepConfigEditorTypes';

interface TaggedUnionTagKeyField {
  kind: 'string';
  value: string;
  required: boolean;
}

function isTaggedUnionChoice(spec: PipelineConfigJsonSpec): spec is TaggedUnionTagKeyField {
  return spec.kind === 'string' && spec.value != null;
}

/** @package */
export function TagChoiceDropdown({
  selectedTag,
  onChangeChoice,
  uiSchema,
  configShape,
  disabled,
  buttonSize,
}: {
  selectedTag: string | null;
  onChangeChoice: (choiceShape: GetPipelineConfigSpecType<'shape'>) => void;
  uiSchema?: TaggedUnionUISchema;
  configShape: GetPipelineConfigSpecType<'taggedUnion'>;
  disabled?: boolean;
  buttonSize?: ButtonSize;
}): JSX.Element {
  const tagKey = configShape.tagKey;

  function getTagOptionDisplayName(tagOption: string): string {
    return uiSchema?.tagKeyDisplayNames?.[tagOption] ?? tagOption;
  }

  const menuOptions: Array<OptionData> = configShape.choices.map((choiceShape) => {
    const tagKeySpec = choiceShape.fields[tagKey];
    assert(tagKeySpec != null && isTaggedUnionChoice(tagKeySpec), 'Invalid tagged union choice found');
    return {
      name: getTagOptionDisplayName(tagKeySpec.value),
      value: tagKeySpec.value,
    };
  });

  const handleSelect = (value: string): void => {
    for (const choice of configShape.choices) {
      const tagKeySpec = choice.fields[tagKey];
      assert(tagKeySpec != null && isTaggedUnionChoice(tagKeySpec), 'Invalid tagged union choice found');
      if (tagKeySpec.value === value) {
        onChangeChoice(choice);
        return;
      }
    }
    assert(false, 'No matching choice found');
  };

  const selector = (
    <SelectDropdown
      className={css(styles.grow)}
      dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', key: tagKey}}
      displayName={
        selectedTag != null
          ? getTagOptionDisplayName(selectedTag)
          : (uiSchema?.placeholder ?? 'Select a value')
      }
      buttonSize={buttonSize}
      menu={
        <SearchableMenu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.MEDIUM}
          optionDatas={menuOptions}
          selectedValue={selectedTag}
          onSelect={handleSelect}
        />
      }
      disabled={disabled}
    />
  );
  return uiSchema?.renderCyclingSelector ? (
    <CyclingSelector
      selector={selector}
      disabled={disabled}
      options={menuOptions.map((option) => option.value)}
      selectedOption={selectedTag}
      onChange={handleSelect}
    />
  ) : (
    selector
  );
}

const styles = StyleSheet.create({
  grow: {
    flexGrow: 1,
  },
});
